import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import Avatar from '@ublend-npm/aulaui-next-web/Avatar';
import LaunchIcon from '@material-ui/icons/Launch';
import { TURNITIN_PROVIDER } from '../Assignments/constants';
import { ENTER } from '../../../../utils/keyCodes';

import {
  Grade,
  FullName,
  AvatarWrapper,
  SubmissionDate,
  VisuallyHidden,
  SubmissionListItem,
  SubmissionHyperLink,
  SubmissionListItemAvatar,
} from './Submissions.styled';
import { analyticsTrackEvent } from '../../../../../core/utils/analytics';
import * as analyticEvents from '../../../../../core/constants/analytics';

const SubmissionsCard = memo(
  ({
    user,
    submissionDate,
    grade,
    provider,
    providerConfig,
    submissionId,
    isGroup,
    assignmentId,
    accessingAsStaff,
  }) => {
    const [isVisible, setIsVisible] = useState(false);

    const onFocus = () => setIsVisible(true);
    const onBlur = () => setIsVisible(false);

    const { handler, title } = providerConfig[provider];
    const isTurnitinSubmission = provider === TURNITIN_PROVIDER;
    const hasSubmission = !!submissionId;
    const { id: userId, avatar, firstName, lastName = '' } = user;
    const fullName = `${firstName} ${lastName}`;
    const canOpenSubmission =
      hasSubmission && (!accessingAsStaff || !isTurnitinSubmission);

    const handleClick = () => {
      if (!canOpenSubmission) {
        return null;
      }

      analyticsTrackEvent(analyticEvents.VIEW_STUDENT_SUBMISSION, {
        assignmentId,
      });

      if (isTurnitinSubmission) {
        return handler({ submissionId });
      }
      return handler({ userId });
    };

    return (
      <SubmissionListItem
        isGroup
        tabIndex="0"
        onClick={handleClick}
        data-testid="submission-item"
        onFocus={onFocus}
        onBlur={onBlur}
        onMouseEnter={onFocus}
        onMouseLeave={onBlur}
        onKeyDown={(e) => e.keyCode === ENTER && handleClick()}
      >
        <AvatarWrapper>
          <SubmissionListItemAvatar>
            <Avatar size="large" name={fullName} src={avatar} />
          </SubmissionListItemAvatar>
        </AvatarWrapper>
        <FullName
          data-testid="submission-item-full-name"
          primary={fullName}
          isGroup={isGroup}
        />
        <SubmissionDate
          data-testid="submission-item-date"
          primary={submissionDate}
        />
        <Grade primary={grade} data-testid="submission-item-grade" />
        {canOpenSubmission && (
          <SubmissionHyperLink
            data-testid="submission-item-link"
            aria-label={`Open in ${title}`}
            primary={
              <span css={!isVisible ? VisuallyHidden : ''}>
                Open in {title}
                <LaunchIcon fontSize="small" />
              </span>
            }
          />
        )}
      </SubmissionListItem>
    );
  }
);

SubmissionsCard.propTypes = {
  submissionDate: PropTypes.string.isRequired,
  grade: PropTypes.string,
  provider: PropTypes.string.isRequired,
  providerConfig: PropTypes.shape({}).isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    avatar: PropTypes.string,
  }),
  submissionId: PropTypes.string,
  assignmentId: PropTypes.string,
  isGroup: PropTypes.bool,
  accessingAsStaff: PropTypes.bool,
};

SubmissionsCard.defaultProps = {
  user: {
    firstName: '',
    lastName: '',
    id: '',
    avatar: '',
  },
  grade: '-',
  submissionId: '',
  assignmentId: '',
  isGroup: false,
  accessingAsStaff: false,
};

export default SubmissionsCard;
